import LanguagePack from "../../../../../lib/html5-software-library/src/ranj/language/LanguagePack";
import RevisableLanguagePack from "../../../../../lib/html5-software-library/src/ranj/language/RevisableLanguagePack";
import LayoutManager from "../../../../../lib/html5-software-library/src/ranj/layout/LayoutManager";
import { getURLParameter } from "../../../../../lib/html5-software-library/src/ranj/util/URLUtil";
import TrackScreenString from "../../../analytics/TrackScreenString";
import TrackString from "../../../analytics/TrackString";
import AbstractMediator from "../../../core/ui/AbstractMediator";
import TextTypeWriterAnimation from "../../../core/ui/animation/typewriters/TextTypeWriterAnimation";
import ClickEaterFactory from "../../../core/ui/ClickEaterFactory";
import SkinKeyRegistry from "../../../core/ui/SkinKeyRegistry";
import HubEvent from "../../hub/event/HubEvent";
import StoryVariables from "../../util/StoryVariables";
import BriefingEvent from "../event/BriefingEvent";
import BriefingSoundType from "../sound/BriefingSoundType";

export default class BriefingScreenMediator extends AbstractMediator {
    /**
     * @constructor
     * @param {createjs.MovieClip} target 
     * @param {soma.EventDispatcher} dispatcher 
     * @param {LayoutManager} layoutManager 
     * @param {ClickEaterFactory} clickEaterFactory 
     * @param {RevisableLanguagePack} languagePack
     * @param {SoundManager} soundManager
     * @param {SoundConfig} soundConfig
     */
    constructor(target, dispatcher, layoutManager, clickEaterFactory, languagePack, soundManager, soundConfig, variableManager, progressGameModel) {
        super(target, dispatcher, layoutManager, clickEaterFactory);

        this._languagePack = languagePack;
        this._variableManager = variableManager;
        this._progressGameModel = progressGameModel;
        this._soundManager = soundManager;
        this._soundConfig = soundConfig;

        this._typeWriterAnimation = null;
        this._chapterNumber = 1;
        this._weekNumber = 1;
        this._speedTypeWriting = 20;
        this._typeWritingCompleted = false;
        this._step = 0;
    }

    initialize() {
        super.initialize();
        this.preventClickThrough();
        this._initContent();
        this._playAudio();
        this._sendEvents();
        this._animateContent();
        if (ranj.DEBUG && getURLParameter('skipbriefing') == 1) {
            setTimeout(() => { this.dispatch(BriefingEvent.CLOSE_REQUEST); }, 1000);
        }
    }

    _initContent() {
        this._weekNumber = this._progressGameModel.week;
        this._chapterNumber = this._progressGameModel.chapter;

        this._target.ChapterContent.text = "";
        this._target.ChapterNumber.alpha = 0;
        this._target.ChapterTitle.alpha = 0;
        this._target.CWeekNumber.alpha = 0;
        this._target.BulletPoint.alpha = 0;

        this._target.ChapterNumber.text = this._languagePack.getText(`chapters.general.briefing.prefixChapterTitle`) + " " + this._chapterNumber;
        this._target.CWeekNumber.text = this._languagePack.getText(`chapters.general.briefing.prefixWeekTitle`) + " " + this._weekNumber;
        this._target.ChapterTitle.text = this._languagePack.getText(`chapters.chapter${this._chapterNumber}.briefing.title`);
        this._continueButton = this.createButton(this._target.ContinueButton, this._handleContinueButton.bind(this), this._languagePack.getText('chapters.general.briefing.continueButtonLabel'));
        this._continueButton.setEnabled(false);
        this._continueButton.skin.alpha = 0;
    }
    _sendEvents() {
        switch (this._chapterNumber) {
            case 1:
                this.trackEvent(TrackString.EVENT_SCREEN_STARTED, { screen_started: TrackScreenString.CHAPTER1_BRIEFING });
                break
            case 2:
                this.trackEvent(TrackString.EVENT_SCREEN_STARTED, { screen_started: TrackScreenString.CHAPTER2_BRIEFING });
                break
            case 3:
                this.trackEvent(TrackString.EVENT_SCREEN_STARTED, { screen_started: TrackScreenString.CHAPTER3_BRIEFING });
                break
        }
    }

    _playAudio() {
        let chapterSound;

        switch (this._chapterNumber) {
            case 1:
                chapterSound = BriefingSoundType.BRIEFING_CHAPTER_1;
                break
            case 2:
                chapterSound = BriefingSoundType.BRIEFING_CHAPTER_2;
                break
            case 3:
                chapterSound = BriefingSoundType.BRIEFING_CHAPTER_3;
                break
        }

        console.log("Playing audio: " + chapterSound + " with volume: " + this._soundConfig.getVolume(chapterSound));
        this._briefingChapterSound = this._soundManager.playSound(chapterSound, null, null, 0, 0, -1, this._soundConfig.getVolume(chapterSound));
    }

    _animateContent() {
        const initDelay = 500;

        // Slide Down Animation
        const slideDownElement = this._target.BriefingTimelineBar;
        const slideDownDelay = 1000;
        const slideDownInitY = slideDownElement.y;
        slideDownElement.y -= (slideDownElement.nominalBounds.height / 2 + 50);
        slideDownElement.scaleY = 0;

        // Slide Right Animation
        const slideRightElement = this._target.ChapterContentContainer;
        const fadeSlideRightDelay = 750;
        const slideRightNegativeDelay = 400;
        const slideRightOffsetX = -50;
        const slideRightInitX = slideRightElement.x;
        slideRightElement.x += slideRightOffsetX;
        slideRightElement.alpha = 0;

        createjs.Tween.get(slideDownElement).wait(initDelay).to({ scaleY: 1, y: slideDownInitY }, slideDownDelay, createjs.Ease.sineOut);
        createjs.Tween.get(slideRightElement).wait(initDelay).wait(slideDownDelay - slideRightNegativeDelay).to({ alpha: 1, x: slideRightInitX }, fadeSlideRightDelay, createjs.Ease.sineInOut).call(() => { this._displayTextContent() });
    }
    _displayTextContent() {
        this._fadeInDelay = 200;
        this._waitText = 2000;
        let waitTitle = 0;
        const els = [this._target.ChapterNumber, this._target.BulletPoint, this._target.CWeekNumber, this._target.ChapterTitle];
        for (let el of els) {
            createjs.Tween.get(el).wait(waitTitle).to({ alpha: 1 }, this._fadeInDelay, createjs.Ease.sineOut);
            waitTitle += (this._waitText / 4);
        }
        if (this._continueButton != null) {
            this._continueButton.setEnabled(true);
            this._timeWriterText(this._languagePack.getText(`chapters.chapter${this._chapterNumber}.briefing.body`), this._waitText);
            createjs.Tween.get(this._continueButton.skin).wait(this._waitText + 400).to({ alpha: 1 }, this._fadeInDelay, createjs.Ease.sineOut);
        }
    }

    _timeWriterText(text, wait = 0, fadeInDelay = 400) {
        this._target.ChapterContent.alpha = 0;
        this._target.ChapterContent.text = text;
        createjs.Tween.get(this._target.ChapterContent).wait(wait).to({ alpha: 1 }, fadeInDelay, createjs.Ease.sineOut);
        //.call(() => {

            /*
            this._typeWriterAnimation = TextTypeWriterAnimation.animate(this._target.ChapterContent, text, this._speedTypeWriting, 0, () => {
                this._typeWritingCompleted = true;
            });*/
            //Disabling Type Writer animation
            //this._typeWriterAnimation.forceFinish();
        //});
    }

    destroy() {
        super.destroy();
        this._continueButton = null;
    }

    _handleContinueButton() {
        if(this._step == 0 && this._languagePack.getText(`chapters.chapter${this._chapterNumber}.briefing.body2`) != null) {
            // In case there another body of content, we display it
            this._step = 1;
            this._waitText = 0;
            this._timeWriterText(this._languagePack.getText(`chapters.chapter${this._chapterNumber}.briefing.body2`), 0, 1200);
        } else {
            this.disableInteraction();
            this._soundManager.stopSound(this._briefingChapterSound);
            this.dispatch(BriefingEvent.CLOSE_REQUEST);
        }
    }
}